import React, { useMemo, useState } from "react";
import { Check, ClipboardCheck, Search, ShieldCheck, X } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction, useData } from "@/hooks/useData";
import {
  Button, DataTable, DefinitionList, Drawer, Field, Input, Loader, PageHeader, PersonCell, Segmented, StatusBadge, TableCard, Textarea,
} from "@/components/ui";
import { dateLabel } from "@/lib/format";

const TABS = [
  { value: "Pending", label: "Pending", tone: "orange" },
  { value: "Approved", label: "Approved", tone: "green" },
  { value: "Rejected", label: "Rejected", tone: "red" },
  { value: "all", label: "All" },
];

export default function LeaveApprovals() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Pending");
  const [q, setQ] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const { data: leaves, reload } = useData(() => api.leaves.list(user), [user]);
  const { run, pending } = useAction();

  const decide = async (id, decision, why) => {
    const { ok } = await run(() => api.leaves.decide(id, decision, user, why), `Leave ${decision.toLowerCase()}`);
    if (ok) { setRejecting(null); setReason(""); reload(); }
  };

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (leaves || []).filter((l) =>
      (tab === "all" || l.status === tab) &&
      (!needle || `${l.employee_name} ${l.department} ${l.leave_type}`.toLowerCase().includes(needle)));
  }, [leaves, tab, q]);

  if (!leaves) return <Loader />;
  const count = (v) => (v === "all" ? leaves.length : leaves.filter((l) => l.status === v).length);

  const columns = [
    { key: "employee", header: "Employee", render: (l) => <PersonCell name={l.employee_name} sub={l.department} /> },
    { key: "type", header: "Type", render: (l) => <span className="text-xs font-semibold">{l.leave_type}</span> },
    { key: "dates", header: "Dates", hide: "md", render: (l) => <span className="text-xs">{dateLabel(l.from_date)}{l.to_date !== l.from_date && ` → ${dateLabel(l.to_date)}`}</span> },
    { key: "days", header: "Days", render: (l) => <span className="text-xs font-extrabold text-brand-blue">{l.days}</span> },
    { key: "reason", header: "Reason", hide: "xl", className: "max-w-[220px]", render: (l) => <p className="truncate text-xs text-slate-500" title={l.reason}>{l.reason}</p> },
    { key: "status", header: "Status", hide: "lg", render: (l) => <StatusBadge status={l.status} /> },
    { key: "actions", header: "", align: "right", render: (l) => l.status === "Pending" ? (
      <div className="flex gap-2 justify-end">
        <Button size="xs" variant="success" disabled={pending} icon={<Check size={12} />} onClick={() => decide(l.id, "Approved")}>Approve</Button>
        <Button size="xs" variant="danger" disabled={pending} icon={<X size={12} />} onClick={() => setRejecting(l)}>Reject</Button>
      </div>
    ) : l.decided_by && <span className="text-[11px] text-slate-400">by {l.decided_by}</span> },
  ];

  return (
    <>
      <Segmented value={tab} onChange={setTab} options={TABS.map((t) => ({ ...t, count: count(t.value) }))} />

      <PageHeader>
        <div className="flex-1 min-w-[220px]">
          <Input icon={Search} placeholder="Search by employee, department or type…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-full" aria-label="Search leave requests" />
        </div>
      </PageHeader>

      <TableCard
        heading={`${rows.length} request${rows.length === 1 ? "" : "s"}`}
        aside={<><ShieldCheck size={13} className="text-brand-orange" /><span className="hidden sm:inline">The employee is emailed on every decision</span></>}
      >
        <DataTable
          columns={columns}
          rows={rows}
          empty={tab === "Pending" ? { icon: ClipboardCheck, title: "You're all caught up", message: "No requests are waiting for approval." } : "No leave requests"}
        />
      </TableCard>

      <Drawer
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Reject leave request"
        subtitle={rejecting?.employee_name}
        footer={<>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button variant="destructive" loading={pending} disabled={!reason.trim()} onClick={() => decide(rejecting.id, "Rejected", reason.trim())}>Reject request</Button>
        </>}
      >
        {rejecting && (
          <div className="space-y-5">
            <div className="neu-inset p-4">
              <DefinitionList rows={[
                { label: "Type", value: rejecting.leave_type },
                { label: "Dates", value: `${dateLabel(rejecting.from_date)} → ${dateLabel(rejecting.to_date)}` },
                { label: "Days", value: rejecting.days },
                { label: "Reason given", value: rejecting.reason },
              ]} />
            </div>
            <Field label="Rejection reason" hint="The employee will see this.">
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </Field>
          </div>
        )}
      </Drawer>
    </>
  );
}
